/**
 * Copy for everything that points AT the tax-iceberg calculator from elsewhere on the site: the
 * promo card (resources hub, salary hub) and the inline salary link widget. Hand-branched per
 * locale like the calculator's own SEO copy, keyword-first (fi "palkkalaskuri",
 * "veroprosentti", "nettopalkka"; sv "löneräknare", "nettolön"; en "salary calculator").
 */

import { taxIcebergLogicalPath, type Period } from './tax-iceberg-url';

export type PromoLocale = 'en' | 'fi' | 'sv';

export function asPromoLocale(locale: string | undefined): PromoLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

export const TAX_ICEBERG_PROMO: Record<PromoLocale, { badge: string; title: string; description: string; cta: string; imageAlt: string }> = {
  en: {
    badge: 'Tax calculator',
    title: 'The Tax Iceberg: how much of your pay sinks below the waterline?',
    description: 'Your payslip is the tip. Employer costs, income tax, contributions and VAT are the mass underneath. Enter a salary and watch your iceberg float in Helsinki harbour.',
    cta: 'Calculate your iceberg',
    imageAlt: 'An iceberg in the sea in front of Helsinki, most of it below the waterline',
  },
  fi: {
    badge: 'Verolaskuri',
    title: 'Verojäävuori: kuinka suuri osa palkastasi uppoaa pinnan alle?',
    description: 'Palkkakuitti näyttää vain huipun. Työnantajan sivukulut, tuloverot, maksut ja arvonlisävero ovat massa pinnan alla. Syötä palkka, näe veroprosenttisi ja nettopalkkasi, ja katso jäävuoresi kellua Helsingin edustalla.',
    cta: 'Laske oma jäävuoresi',
    imageAlt: 'Jäävuori Helsingin edustan merellä, suurin osa vedenpinnan alla',
  },
  sv: {
    badge: 'Skatteräknare',
    title: 'Skatteisberget: hur stor del av din lön sjunker under ytan?',
    description: 'Lönebeskedet är bara toppen. Arbetsgivaravgifter, inkomstskatt, avgifter och moms är massan under ytan. Fyll i en lön, se din skatteprocent och nettolön, och se ditt isberg flyta utanför Helsingfors.',
    cta: 'Räkna ut ditt isberg',
    imageAlt: 'Ett isberg i havet utanför Helsingfors, mestadels under vattenytan',
  },
};

/** Text of the inline widget shown next to a salary figure. `amount` is already formatted. */
export function taxIcebergLinkLabel(locale: PromoLocale, amount: string | null, period: Period = 'month'): string {
  const month = period === 'month';
  if (locale === 'fi') return amount ? `Mitä ${amount} ${month ? 'kuukaudessa' : 'vuodessa'} jää käteen?` : 'Laske nettopalkka ja veroprosentti';
  if (locale === 'sv') return amount ? `Vad blir ${amount} ${month ? 'i månaden' : 'om året'} netto?` : 'Räkna ut nettolön och skatteprocent';
  return amount ? `What does ${amount} ${month ? 'a month' : 'a year'} leave you?` : 'Net salary and tax rate calculator';
}

/** Logical path (no locale prefix) for the calculator, optionally preset to a salary. */
export function taxIcebergHref(locale: string | undefined, grossMonthly?: number): string {
  const rounded = grossMonthly && grossMonthly > 0 ? Math.round(grossMonthly / 50) * 50 : undefined;
  return taxIcebergLogicalPath(locale, rounded, 'month');
}
